import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const Giverportal = () => {
  const [params] = useSearchParams();
  const [epost, setEpost] = useState('');
  const [melding, setMelding] = useState('');
  const [token, setToken] = useState(() => localStorage.getItem('giver_token') || '');
  const [meg, setMeg] = useState(null);
  const [donasjoner, setDonasjoner] = useState([]);
  const [valgtAar, setValgtAar] = useState(new Date().getFullYear() - 1);

  const cli = axios.create({
    baseURL: '/api/giverportal',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  const last = useCallback(async () => {
    if (!token) return;
    try {
      const m = await axios.get('/api/giverportal/me', { headers: { Authorization: `Bearer ${token}` } });
      setMeg(m.data);
      const d = await axios.get('/api/giverportal/donasjoner', { headers: { Authorization: `Bearer ${token}` } });
      setDonasjoner(d.data);
    } catch (e) {
      localStorage.removeItem('giver_token');
      setToken('');
    }
  }, [token]);

  useEffect(() => { last(); }, [last]);

  useEffect(() => {
    const t = params.get('token');
    if (t) {
      axios.post('/api/giverportal/login', { token: t })
        .then((r) => {
          localStorage.setItem('giver_token', r.data.access_token);
          setToken(r.data.access_token);
          window.history.replaceState({}, '', '/giverportal');
        })
        .catch(() => setMelding('Ugyldig eller utløpt lenke.'));
    }
  }, [params]);

  const sendLink = async (e) => {
    e.preventDefault();
    if (!epost.trim()) return;
    try {
      await axios.post('/api/giverportal/magic-link', { epost });
      setMelding('Sjekk e-posten din — vi har sendt en innloggingslenke.');
    } catch (err) {
      setMelding('Klarte ikke sende lenke: ' + (err.response?.data?.error || err.message));
    }
  };

  const lastAarsoppgave = async () => {
    try {
      const r = await axios.get(`/api/giverportal/aarsoppgave/${valgtAar}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `aarsoppgave_${valgtAar}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('Feil: ' + (e.response?.data?.error || e.message));
    }
  };

  const loggUt = () => {
    localStorage.removeItem('giver_token');
    setToken(''); setMeg(null); setDonasjoner([]);
  };

  if (!token || !meg) {
    return (
      <div style={{ maxWidth: 460, margin: '80px auto', padding: 24, fontFamily: 'system-ui' }}>
        <h1>Giverportal</h1>
        <p style={{ color: '#666' }}>
          Logg inn med e-postadressen din for å se gavehistorikken og laste ned årsoppgaven.
        </p>
        <form onSubmit={sendLink}>
          <input
            type="email" required value={epost}
            onChange={(e) => setEpost(e.target.value)}
            placeholder="din@epost.no"
            style={{ width: '100%', padding: 12, fontSize: 16, marginBottom: 12, border: '1px solid #ccc', borderRadius: 6 }}
          />
          <button type="submit" style={{ width: '100%', padding: 12, fontSize: 16, background: '#0066cc', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer' }}>
            Send innloggingslenke
          </button>
        </form>
        {melding && <p style={{ marginTop: 16, color: '#0a0' }}>{melding}</p>}
      </div>
    );
  }

  const total = donasjoner.reduce((s, d) => s + (d.belop || 0), 0);

  return (
    <div style={{ maxWidth: 760, margin: '40px auto', padding: 24, fontFamily: 'system-ui' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1>Min giverside</h1>
        <button onClick={loggUt} style={{ background: 'transparent', border: '1px solid #ccc', padding: '6px 12px', borderRadius: 6, cursor: 'pointer' }}>Logg ut</button>
      </div>
      <p style={{ color: '#666' }}>{meg.epost}</p>

      <div style={{ background: '#f6f8fa', padding: 20, borderRadius: 8, margin: '24px 0' }}>
        <h3 style={{ margin: 0 }}>Totalt gitt: {total.toLocaleString('nb-NO')} kr</h3>
        <p style={{ marginTop: 6, color: '#666' }}>{donasjoner.length} donasjoner</p>
      </div>

      <h2>Årsoppgave for skattefradrag</h2>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 24 }}>
        <select value={valgtAar} onChange={(e) => setValgtAar(Number(e.target.value))} style={{ padding: 8 }}>
          {[0, 1, 2, 3, 4].map((n) => {
            const a = new Date().getFullYear() - n;
            return <option key={a} value={a}>{a}</option>;
          })}
        </select>
        <button onClick={lastAarsoppgave} style={{ padding: '8px 16px', background: '#0066cc', color: '#fff', border: 0, borderRadius: 6, cursor: 'pointer' }}>
          Last ned PDF
        </button>
      </div>

      <h2>Donasjonshistorikk</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ccc' }}>
            <th style={{ textAlign: 'left', padding: 8 }}>Dato</th>
            <th style={{ textAlign: 'left', padding: 8 }}>Kampanje</th>
            <th style={{ textAlign: 'right', padding: 8 }}>Beløp</th>
            <th style={{ textAlign: 'center', padding: 8 }}>Skattefradrag</th>
          </tr>
        </thead>
        <tbody>
          {donasjoner.map((d) => (
            <tr key={d.id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: 8 }}>{d.dato}</td>
              <td style={{ padding: 8 }}>{d.kampanje || '—'}</td>
              <td style={{ padding: 8, textAlign: 'right' }}>{d.belop?.toLocaleString('nb-NO')} kr</td>
              <td style={{ padding: 8, textAlign: 'center' }}>{d.skattefradrag ? '✓' : ''}</td>
            </tr>
          ))}
          {donasjoner.length === 0 && (
            <tr><td colSpan={4} style={{ padding: 24, textAlign: 'center', color: '#999' }}>Ingen donasjoner registrert.</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Giverportal;
